import { buildApiUrl } from './getServerInfo';

/**
 * buildStreamUrl — build a playback clip stream URL for the camstream `cmd=fetch` endpoint.
 *
 * Extracted from the legacy PlayerWindow.buildVideoUrl so MCPlayback can reuse the
 * exact same auth-by-context branches. Three branches:
 *   - CloudVS  → `${cloudAPI}${file}?token=${sApiToken}`            (wins over eview/dview)
 *   - eView    → session auth for the serving NVR, user/pass auth for another member NVR.
 *   - dView    → session auth for the current server.
 * Returns '' when there is no clip file.
 *
 * @param {object} ctx
 * @param {string}  ctx.file               clip path (legacy prop: currentClipPlayingUrl)
 * @param {boolean} ctx.cloud              CloudVS clip (legacy prop: currentClipPlayingCloud)
 * @param {string}  ctx.cloudAPI
 * @param {string}  ctx.sApiToken
 * @param {boolean} ctx.fEview
 * @param {string}  ctx.playbackServerUrl  e.g. 'http://host:8080/JSON/' (the /JSON is stripped)
 * @param {number}  ctx.playbackServerSerial serial that owns this clip
 * @param {number}  ctx.currentServerSerial  serial that minted ctx.sSess
 * @param {string}  ctx.username
 * @param {string}  ctx.password
 * @param {string}  ctx.sSess
 * @param {boolean} [ctx.overlay]          Server-burned OSD for DOWNLOADS (dview/eview only): appends
 *                                          `&overlay=1&mp4=1` so pbserver burns the on-screen display —
 *                                          camera name + timestamp + POS register text (+ a drive-time box)
 *                                          — INTO the clip (handler.c check_overlay → forks mpengine
 *                                          `postoverlay`, which drawtext's name/dtime/reg from the B2
 *                                          header). NOT AI detection boxes. Used for the download URL so the
 *                                          SAVED file carries the OSD; the playback stream stays
 *                                          overlay-free (the viewer draws name/timestamp client-side).
 *                                          Cloud/dragon ignore it (no pbserver/postoverlay).
 * @param {number}  [ctx.profile]          OPTIONAL multi-cam decode-relief hint. >1 appends `&profile=2`.
 *                                          Current nvr-docker installs record profile 2 by default, so
 *                                          pbserver normally serves the recorded H.264 sidecar without
 *                                          re-encoding; the C pbserver creates a 480p fallback when a
 *                                          sidecar is missing (transcode.c PRESERVES source aspect — not a
 *                                          4:3 substream; substreams are a LIVE concept). Old Python
 *                                          pbserver builds (e.g. dvs6.2 on Rosas) ignore the hint and serve
 *                                          full-res profile 1 — so playback aspect stays ~16:9 regardless of
 *                                          profile. CloudVS does not use this parameter.
 * @returns {string}
 */
export const buildStreamUrl = (ctx = {}) => {
  const {
    file,
    cloud,
    cloudAPI,
    sApiToken,
    fEview,
    playbackServerUrl,
    playbackServerSerial,
    currentServerSerial,
    username,
    password,
    sSess,
    profile,
    overlay,
  } = ctx;

  if (!file) {
    return '';
  }

  // A fully-resolved absolute URL (e.g. a cloud recorded-clip MP4 built by
  // CloudNvrAdapter._mapPlaybackEvent) plays straight through — port of Java
  // MCPlayerController's `sFile.startsWith("http")` branch. A <video>/media element
  // loads it cross-origin without a CORS preflight, so no proxy is needed.
  if (/^https?:\/\//i.test(file)) {
    return file;
  }

  // CloudVS videos use the cloud API with a token.
  if (cloud) {
    return `${cloudAPI}${file}?token=${sApiToken}`;
  }

  // Multi-cam decode-relief hint. Current nvr-docker normally serves the recorded
  // profile 2 sidecar. The C pbserver transcodes only when that sidecar is absent.
  // Old Python pbserver builds ignore the parameter and serve profile 1.
  const profileSuffix = Number(profile) > 1 ? `&profile=${Number(profile)}` : '';

  // Server-burned OSD for DOWNLOADS (dview/eview). With &overlay=1&mp4=1, pbserver's cmd=fetch burns the
  // on-screen display — camera name + timestamp + POS register text (+ a drive-time box) — INTO the clip
  // (handler.c check_overlay → forks mpengine postoverlay, which drawtext's name/dtime/reg from the B2
  // header; NOT AI detection boxes) and caches it as <clip>.overlay.mp4. Used ONLY for the download URL so
  // the saved file carries the OSD; the playback STREAM stays overlay-free (the viewer draws name/timestamp
  // client-side). The first request per clip pays the render (postoverlay runs synchronously in the handler
  // → the fetch just WAITS, then streams); after that it is cached. Cloud/dragon ignore this (no postoverlay).
  const overlaySuffix = overlay ? '&overlay=1&mp4=1' : '';

  // Match Java dview's per-slot auth choice: an eView camera on the NVR that
  // minted this session uses session auth. Only a different member NVR needs
  // user/pass credentials.
  const isServingNvr = playbackServerSerial != null &&
    currentServerSerial != null &&
    String(playbackServerSerial) === String(currentServerSerial);
  if (fEview && !isServingNvr) {
    if (playbackServerUrl) {
      const baseUrl = playbackServerUrl.replace(/\/JSON\/?$/, '');
      return `${baseUrl}/camstream/?cmd=fetch&user=${encodeURIComponent(username)}&pass=${encodeURIComponent(password)}&file=${file}${profileSuffix}${overlaySuffix}`;
    }
    // Fallback to a relative URL with user/pass.
    return `/camstream/?cmd=fetch&user=${encodeURIComponent(username)}&pass=${encodeURIComponent(password)}&file=${file}${profileSuffix}${overlaySuffix}`;
  }

  // dView mode — session auth for the current server. Route the clip URL through buildApiUrl so it targets
  // the SAME NVR that minted sSess — including after a Jump System (buildApiUrl reads jumpedServerUrl and
  // resolves it exactly like live: dev → `/server-check/<host:port>/…`, prod → the jumped base, or a plain
  // relative URL when we haven't jumped). Without this the bare relative `/camstream/` hit the dev proxy's
  // BASE target (the pre-jump NVR) after a jump, so that NVR's pbserver rejected this server's session with
  // HTTP 500 "Auth Failed" (playback never played on a jumped system in dev — #86bb7phhh, 2026-08-05).
  return buildApiUrl('/camstream/?cmd=fetch&session=' + sSess + '&file=' + file + profileSuffix + overlaySuffix);
};
